import { useCallback, useState } from 'react'

import MobileLabelLine from 'components/MobileWhiteCardLine'
import {
  SoftwareMobileTitle,
  SoftwareMobileContainer,
  SoftwareUpdatesMobileKebabContainer,
  SoftwareUpdatesMobileInstallWrapper,
  SoftwareMobileInstallContainer,
  SoftwareMobileProgressContainer,
  SoftwareMobileProgressBar,
  SoftwareUpdatesMobileInstallContainer,
} from '../styles'
import type { HomeSoftwareType } from 'features/home/types'
import { DATETIME_SHORT, formatTime } from 'app/utils/formatDate'
import { SoftwareVersionCell } from './SoftwareVersionCell'
import { KebabMenu } from 'components/KebabMenu'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { selectedHomeSelector } from 'features/home/selectors'
import { fetchCloudSoftwareHistoryThunk } from '../../HomeCloudSoftwareHistory/slice'
import { DownloadButtonCell } from 'components/Table'
import { hasSystemAccessEnabled, releaseSetFilename } from 'features/home/utils'
import { SoftwareUpdateActionsCell } from '../../HomeAvailableSoftwareUpdatesTable'
import { useHostIsOffline } from '../../utils'
import { installHomeSoftwareUpdateThunk } from '../../slice'

interface HomeSoftwareUpdatesMobileProps {
  original: HomeSoftwareType
  values: Record<string, any>
}

function HomeSoftwareUpdatesMobile({
  original,
  values,
}: HomeSoftwareUpdatesMobileProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const home = useAppSelector(selectedHomeSelector.data)
  const dispatch = useAppDispatch()
  const hostIsOffline = useHostIsOffline()

  const {
    id,
    partNumber,
    location: { url },
  } = original

  const hasSystemAccess = hasSystemAccessEnabled(home)

  const handleDeploy = useCallback(async () => {
    if (home?.id) {
      setIsLoading(true)
      try {
        await dispatch(
          installHomeSoftwareUpdateThunk(
            home.id,
            partNumber,
            {
              componentSoftwareId: id,
              clearConfig: false,
            },
            setProgress
          )
        )

        await dispatch(
          fetchCloudSoftwareHistoryThunk({
            homeId: home.id,
            partNumber: home.hostType,
          })
        )
      } catch (e) {
        console.log('Error install home software update', e)
        throw e
      } finally {
        setIsLoading(false)
        setProgress(0)
      }
    }
  }, [dispatch, home?.id, id, partNumber, home?.hostType])
  const fileName = releaseSetFilename(url) ?? partNumber

  // todo: localization

  return (
    <SoftwareMobileContainer progress={progress}>
      <SoftwareMobileTitle>
        <SoftwareVersionCell version={original.version.name} />
        {!isLoading && (
          <KebabMenu>
            <SoftwareUpdatesMobileKebabContainer>
              <SoftwareUpdatesMobileInstallContainer>
                <SoftwareUpdatesMobileInstallWrapper
                  onClick={handleDeploy}
                  disabled={!hasSystemAccess || !!hostIsOffline}
                >
                  Install
                </SoftwareUpdatesMobileInstallWrapper>
                <SoftwareUpdateActionsCell
                  software={original}
                  isOnlyWarnShown
                />
              </SoftwareUpdatesMobileInstallContainer>
              <DownloadButtonCell url={url} fileName={fileName}>
                Download
              </DownloadButtonCell>
            </SoftwareUpdatesMobileKebabContainer>
          </KebabMenu>
        )}
      </SoftwareMobileTitle>

      <MobileLabelLine
        label="Released"
        value={formatTime(values.created * 1000, DATETIME_SHORT)}
      />
      {progress ? (
        <SoftwareMobileInstallContainer>
          <span>Installing...</span>
          <SoftwareMobileProgressContainer>
            <SoftwareMobileProgressBar progress={progress} />
          </SoftwareMobileProgressContainer>
        </SoftwareMobileInstallContainer>
      ) : (
        <></>
      )}
    </SoftwareMobileContainer>
  )
}

export default HomeSoftwareUpdatesMobile
